import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input, Caption1, Select, SelectTrigger, Label } from "@activecollab/components";
import PropTypes from "prop-types";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import {
  addBillingPaymentMethod,
  editBillingPaymentMethod,
  taxRatesCheck,
} from "~/store/actions/actions.billingPaymentMethod";
import { fetchAllCountries, fetchAllEUCountries, fetchAllStates } from "~/store/actions/actions.countriesAndStates";
import { getBillingAddress } from "~/store/selectors/selector.billingPaymentMethod";
import {
  makeCountriesAsSelectOptionsSelector,
  makeIsEUCountryByKeySelector,
  makeStatesByCountryAsSelectOptionsSelector,
} from "~/store/selectors/selector.countriesAndStates";
import { getIsBillingInfoAvailable } from "~/store/selectors/selector.paymentMethod";
import Angie from "~/utils/angie";
import { containsOnlyNumbers, isValidEmail, removeNonAlphanumeric } from "~/utils/utils";

const BillingAddressForm = ({ onSubmit, onError, isValidated, shouldShowSuccessMessage }) => {
  const billingAddress = useSelector((state) => getBillingAddress(state));
  const [email, setEmail] = useState(billingAddress ? billingAddress.email : "");
  const [name, setName] = useState(billingAddress ? billingAddress.name : "");
  const [country, setCountry] = useState(billingAddress ? billingAddress.country : "");
  const [address, setAddress] = useState(billingAddress ? billingAddress.address : "");
  const [city, setCity] = useState(billingAddress ? billingAddress.city : "");
  const [zipCode, setZipCode] = useState(billingAddress ? billingAddress.zipCode : "");
  const [state, setState] = useState(billingAddress ? billingAddress.region : "");
  const [vatId, setVatId] = useState(billingAddress ? billingAddress.vatId : "");
  const isBillingInfoAvailable = useSelector((state) => getIsBillingInfoAvailable(state));
  const getCountriesAsSelectOptionsSelector = useMemo(() => {
    return makeCountriesAsSelectOptionsSelector();
  }, []);
  const countriesAsSelectOptionsSelector = useSelector((state) => getCountriesAsSelectOptionsSelector(state));
  const getStatesByCountryAsSelectOptionsSelector = useMemo(() => {
    return makeStatesByCountryAsSelectOptionsSelector();
  }, []);
  const statesByCountryAsSelectOptionsSelector = useSelector((state) =>
    getStatesByCountryAsSelectOptionsSelector(state, country)
  );

  const isStateRequired = useMemo(
    () => statesByCountryAsSelectOptionsSelector?.length > 0,
    [statesByCountryAsSelectOptionsSelector?.length]
  );

  const [isEmailValid, setIsEmailValid] = useState(!!billingAddress.email);
  const [isNameValid, setIsNameValid] = useState(!!billingAddress.name);
  const [isCountryValid, setIsCountryValid] = useState(!!billingAddress.country);
  const [isAddressValid, setIsAddressValid] = useState(!!billingAddress.address);
  const [isCityValid, setIsCityValid] = useState(!!billingAddress.city);
  const [isZipCodeValid, setIsZipCodeValid] = useState(!!billingAddress.zipCode);
  const [isStateValid, setIsStateValid] = useState(true);
  const [isVatIdValid, setIsVatIdValid] = useState(true);

  const saveBillingPaymentMethod = isBillingInfoAvailable ? editBillingPaymentMethod : addBillingPaymentMethod;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAllCountries());
    dispatch(fetchAllStates());
    dispatch(fetchAllEUCountries());
    isValidated(
      isNameValid &&
        isCountryValid &&
        isEmailValid &&
        isAddressValid &&
        isCityValid &&
        isStateValid &&
        isZipCodeValid &&
        isVatIdValid
    );
  }, [
    dispatch,
    isAddressValid,
    isCityValid,
    isCountryValid,
    isEmailValid,
    isNameValid,
    isStateValid,
    isValidated,
    isVatIdValid,
    isZipCodeValid,
  ]);

  const getIsEUCountry = useMemo(() => makeIsEUCountryByKeySelector(), []);
  const isEuCountry = useSelector((state) => getIsEUCountry(state, country));

  const countryName = useMemo(() => {
    return country ? countriesAsSelectOptionsSelector.find((item) => item.id === country)?.name : "Please select...";
  }, [countriesAsSelectOptionsSelector, country]);

  const stateName = useMemo(() => {
    return state ? statesByCountryAsSelectOptionsSelector.find((item) => item.id === state)?.name : "Please select...";
  }, [state, statesByCountryAsSelectOptionsSelector]);

  const onEmailChange = useCallback(
    (e) => {
      const value = e.target.value;
      setEmail(value);
      if (isValidEmail(value)) {
        setIsEmailValid(true);
        return isValidated(
          isNameValid &&
            isCountryValid &&
            isAddressValid &&
            isCityValid &&
            isStateValid &&
            isZipCodeValid &&
            isVatIdValid
        );
      }

      return isValidated(false);
    },
    [isAddressValid, isCityValid, isCountryValid, isNameValid, isStateValid, isValidated, isVatIdValid, isZipCodeValid]
  );
  const onNameChange = useCallback(
    (e) => {
      const value = e.target.value;
      setName(value);
      if (value) {
        setIsNameValid(true);
        return isValidated(
          isEmailValid &&
            isCountryValid &&
            isAddressValid &&
            isCityValid &&
            isStateValid &&
            isZipCodeValid &&
            isVatIdValid
        );
      }

      return isValidated(false);
    },
    [isAddressValid, isCityValid, isCountryValid, isEmailValid, isStateValid, isValidated, isVatIdValid, isZipCodeValid]
  );
  const onCountryChange = useCallback(
    (id) => {
      setCountry(id);
      setState("");
      setIsStateValid(["US", "CA", "AU"].indexOf(id) === -1);
      setVatId("");
      if (id) {
        setIsCountryValid(true);
        return isValidated(
          isEmailValid && isNameValid && isAddressValid && isCityValid && isStateValid && isZipCodeValid && isVatIdValid
        );
      }

      return isValidated(false);
    },
    [isAddressValid, isCityValid, isEmailValid, isNameValid, isStateValid, isValidated, isVatIdValid, isZipCodeValid]
  );
  const onStateChange = useCallback(
    (id) => {
      setState(id);
      if (id) {
        setIsStateValid(true);
        return isValidated(
          isEmailValid &&
            isNameValid &&
            isAddressValid &&
            isCityValid &&
            isCountryValid &&
            isZipCodeValid &&
            isVatIdValid
        );
      }

      return isValidated(false);
    },
    [isAddressValid, isCityValid, isEmailValid, isNameValid, isCountryValid, isValidated, isVatIdValid, isZipCodeValid]
  );
  const onStateInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setState(value);
      setIsStateValid(true);
      return isValidated(
        isEmailValid && isNameValid && isAddressValid && isCityValid && isCountryValid && isZipCodeValid && isVatIdValid
      );
    },
    [isAddressValid, isCityValid, isEmailValid, isNameValid, isCountryValid, isValidated, isVatIdValid, isZipCodeValid]
  );
  const onAddressChange = useCallback(
    (e) => {
      const value = e.target.value;
      setAddress(value);
      if (value) {
        setIsAddressValid(true);
        return isValidated(
          isEmailValid && isNameValid && isStateValid && isCityValid && isCountryValid && isZipCodeValid && isVatIdValid
        );
      }

      return isValidated(false);
    },
    [isStateValid, isCityValid, isEmailValid, isNameValid, isCountryValid, isValidated, isVatIdValid, isZipCodeValid]
  );
  const onCityChange = useCallback(
    (e) => {
      const value = e.target.value;
      setCity(value);
      if (value) {
        setIsCityValid(true);
        return isValidated(
          isEmailValid &&
            isNameValid &&
            isStateValid &&
            isAddressValid &&
            isCountryValid &&
            isZipCodeValid &&
            isVatIdValid
        );
      }

      return isValidated(false);
    },
    [isStateValid, isAddressValid, isEmailValid, isNameValid, isCountryValid, isValidated, isVatIdValid, isZipCodeValid]
  );
  const onZipCodeChange = useCallback(
    (e) => {
      const value = e.target.value;
      setZipCode(value);
      if (value) {
        setIsZipCodeValid(true);
        return isValidated(
          isEmailValid && isNameValid && isStateValid && isAddressValid && isCountryValid && isCityValid && isVatIdValid
        );
      }

      return isValidated(false);
    },
    [isStateValid, isAddressValid, isEmailValid, isNameValid, isCountryValid, isValidated, isVatIdValid, isCityValid]
  );
  const onVatIdChange = useCallback(
    (e) => {
      let value = e.target.value;
      value = removeNonAlphanumeric(value);
      if (containsOnlyNumbers(value)) {
        // handle specific Greece vat id prefix
        if (country === `GR`) {
          value = `EL` + value;
        } else {
          value = country + value;
        }
      }
      setVatId(value);
    },
    [country]
  );
  const dispatchUpdateBillingAddress = useCallback(
    (body) => {
      dispatch(saveBillingPaymentMethod(body)).then((result) => {
        if (result && !result.error) {
          if (shouldShowSuccessMessage) {
            showSuccessToast("Billing details updated.");
          }
          onSubmit();
        }
        if (result && result.error) {
          onError();
          Angie.logger.error("Error in billing details update", {
            error: result.error,
          });
          if (result.error.response.message) {
            showErrorToast(result.error.response.message);
          } else {
            showErrorToast("Failed to update billing details.");
          }
        }
      });
    },
    [dispatch, onError, onSubmit, saveBillingPaymentMethod, shouldShowSuccessMessage]
  );
  const onSubmitCallback = useCallback(
    (e) => {
      e.preventDefault();
      const body = {
        name: name,
        email: email,
        vat: vatId,
        country: country,
        address: address,
        city: city,
        region: state,
        zip_code: zipCode,
      };
      if (isBillingInfoAvailable && vatId) {
        dispatch(taxRatesCheck(body)).then((result) => {
          if (result && !result.error) {
            setIsVatIdValid(true);
            return dispatchUpdateBillingAddress(body);
          } else {
            return setIsVatIdValid(false);
          }
        });
      } else {
        return dispatchUpdateBillingAddress(body);
      }
    },
    [
      address,
      city,
      country,
      dispatch,
      dispatchUpdateBillingAddress,
      email,
      isBillingInfoAvailable,
      name,
      state,
      vatId,
      zipCode,
    ]
  );

  return (
    <form
      id="billing_address_form"
      onSubmit={onSubmitCallback}
      className="tw-max-w-[300px]"
      data-testid="submit-billing-address"
    >
      <div className="tw-mb-3">
        <Label
          htmlFor="billing-address-email"
          size="small"
        >
          Email for Invoices
        </Label>
        <Input
          data-testid="billing-address-email"
          id="billing-address-email"
          type="email"
          className="tw-w-full tw-p-2"
          onChange={onEmailChange}
          value={email}
          placeholder="Email for Invoices"
        />
        <Caption1 color="tertiary">Invoices will be delivered to this email address.</Caption1>
      </div>
      <div className="tw-mb-3">
        <Label
          htmlFor="billing-address-company"
          size="small"
        >
          Name
        </Label>
        <Input
          data-testid="billing-address-company"
          id="billing-address-company"
          className="tw-w-full tw-p-2"
          onChange={onNameChange}
          value={name}
          placeholder="Personal or Company name"
        />
        <Caption1 color="tertiary">Recipient of the Invoice (Personal or Company name)</Caption1>
      </div>
      <div
        className="tw-mb-3"
        data-testid="billing-address-select-country"
      >
        <Label size="small">Country</Label>
        <Select
          options={countriesAsSelectOptionsSelector}
          selected={country}
          target={<SelectTrigger className="tw-w-full">{countryName}</SelectTrigger>}
          onChange={onCountryChange}
          forceCloseMenu
        />
      </div>
      <div className="tw-mb-3">
        <Label
          htmlFor="billing-address-user-address"
          size="small"
        >
          Address
        </Label>
        <Input
          data-testid="billing-address-user-address"
          id="billing-address-user-address"
          className="tw-w-full tw-p-2"
          onChange={onAddressChange}
          value={address}
          placeholder="Street Address"
        />
      </div>
      <div className="tw-mb-3">
        <Input
          data-testid="billing-address-city"
          className="tw-w-full tw-p-2"
          onChange={onCityChange}
          value={city}
          placeholder="City"
        />
      </div>
      <div className="tw-flex tw-mb-3 tw-align-middle tw-justify-between">
        {isStateRequired ? (
          <div
            className="tw-w-[170px]"
            data-testid="billing-address-country-states"
          >
            <Select
              options={statesByCountryAsSelectOptionsSelector}
              selected={country}
              target={<SelectTrigger className="tw-w-full">{stateName}</SelectTrigger>}
              onChange={onStateChange}
              forceCloseMenu
            />
          </div>
        ) : (
          <Input
            data-testid="billing-address-state"
            className="tw-w-[170px] tw-mr-5"
            onChange={onStateInputChange}
            placeholder="State / Province"
            value={state}
          />
        )}
        <Input
          data-testid="billing-address-zip-code"
          className="tw-w-[110px] tw-border-gray-300"
          onChange={onZipCodeChange}
          placeholder="Zip / Postal Code"
          value={zipCode}
        />
      </div>
      {isEuCountry ? (
        <div className="tw-mb-3 tw-border">
          <Label
            htmlFor="vat-id"
            invalid={!isVatIdValid}
            size="small"
          >
            VAT ID
          </Label>
          <Input
            className="tw-w-full tw-p-2"
            id="vat-id"
            onChange={onVatIdChange}
            placeholder="(optional)"
            value={vatId}
            invalid={!isVatIdValid}
            data-testid="vat-code"
          />
          <Caption1 color="tertiary">Provide a valid VAT ID to remove tax from your invoice.</Caption1>
        </div>
      ) : null}
    </form>
  );
};

BillingAddressForm.propTypes = {
  formId: PropTypes.string,
  isValidated: PropTypes.func,
  onSubmit: PropTypes.func,
  onError: PropTypes.func,
  shouldShowSuccessMessage: PropTypes.bool,
};

BillingAddressForm.defaultProps = {
  formId: "billing_address_form",
  shouldShowSuccessMessage: true,
};

export default BillingAddressForm;
